import ActiveSpokeInfo from 'mini-calendar-event-common-view/lib/schema/ActiveSpokeInfo';
import BusyType from 'owa-service/lib/contract/BusyType';
import ColorScheme from 'mini-color-utils/lib/ColorScheme';
import EventScope from 'owa-service/lib/contract/EventScope';
import { action } from 'satcheljs';
import { ActionSource, addDatapointConfig } from 'owa-analytics-actions';
import { AppContext } from 'mini-app-context';
import { CALENDAR_READING_PANE } from 'mini-calendar-utils/lib/eventSourcesConstants';
import { CalendarEvent } from 'mini-calendar-event-types';
import { GetCalendarEventsCallbackType } from 'mini-calendar-event-common-view/lib/schema/EventDetailsPropsFromConsumer.types';

export const readingPaneEventDelete = action(
    'READING_PANE_EVENT_DELETE',
    (
        getCancellationEditor: (
            onTextEdit: (notes: string) => void,
            sendCancellationPlaceholder: string
        ) => JSX.Element,
        isCancellableEvent: boolean,
        colorScheme?: ColorScheme,
        cleanupAction?: (event: CalendarEvent) => void,
        scope?: EventScope,
        ctx?: AppContext
    ) => ({ getCancellationEditor, isCancellableEvent, colorScheme, cleanupAction, scope, ctx })
);

/**
 * Action to initiate calendar event update from reading pane
 */
export const onReadingPaneEventUpdateStart = action(
    'READING_PANE_EVENT_UPDATE_START',
    (calendarEventUpdates: Partial<CalendarEvent>, scope: EventScope) => ({
        calendarEventUpdates,
        scope,
    })
);

/**
 * Action which triggers the start edit of show as state.
 */
export const editShowAsStateStart = action(
    'EDIT_SHOW_AS_STATE_START',
    (
        calendarEvent: CalendarEvent,
        sheetRef: React.RefObject<any>,
        showAsBottomSheet: (event: CalendarEvent) => JSX.Element
    ) => ({
        calendarEvent,
        sheetRef,
        showAsBottomSheet,
    })
);

/**
 * Action which is raised when the edit of show as state is completed.
 */
export const editShowAsStateCompleted = action(
    'EDIT_SHOW_AS_STATE_COMPLETED',
    (showAsState: BusyType, event: CalendarEvent, sheetRef: React.RefObject<any>) =>
        addDatapointConfig(
            {
                name: 'ShowAsValueChanged',
                actionSource: CALENDAR_READING_PANE as ActionSource,
                customData: {
                    isUIAction: true,
                    eventId: event && event.ItemId && event.ItemId.Id,
                    showAs: showAsState,
                },
            },
            { showAsState, sheetRef }
        )
);

/**
 * Action which triggers the start edit of reminder option.
 */
export const editReminderOptionStart = action(
    'EDIT_REMINDER_OPTION_START',
    (
        calendarEvent: CalendarEvent,
        sheetRef: React.RefObject<any>,
        reminderOptionComponent: (key: number) => JSX.Element
    ) => ({
        calendarEvent,
        sheetRef,
        reminderOptionComponent,
    })
);

/**
 * Action which is raised when the edit of reminder option is completed.
 */
export const editReminderOptionCompleted = action(
    'EDIT_REMINDER_OPTION_COMPLETED',
    (remindMeOption: number, event: CalendarEvent, sheetRef: React.RefObject<any>) =>
        addDatapointConfig(
            {
                name: 'RemindMeValueChanged',
                actionSource: CALENDAR_READING_PANE as ActionSource,
                customData: {
                    isUIAction: true,
                    eventId: event && event.ItemId && event.ItemId.Id,
                    reminder: remindMeOption,
                },
            },
            { remindMeOption, sheetRef }
        )
);

/**
 * Action fired when user clicks on email attendees button in reading pane
 */
export const emailAttendees = action(
    'EMAIL_ATTENDEES',
    (item: CalendarEvent, onOpenSpoke?: (activeSpokeInfo: ActiveSpokeInfo) => void) => ({
        item,
        onOpenSpoke,
    })
);

/**
 * Action fired to initialize event attendees message for an event
 */
export const initializeEventAttendeesReplyMessage = action(
    'INITIALIZE_EVENT_ATTENDEES_REPLY_MESSAGE',
    (item: CalendarEvent, scope: EventScope) => ({
        item,
        scope,
    })
);

/**
 * Action fired when user clicks on forward invitation button in reading pane
 */
export const forwardEvent = action(
    'FORWARD_EVENT',
    (item: CalendarEvent, onOpenSpoke?: (activeSpokeInfo: ActiveSpokeInfo) => void) => ({
        item,
        onOpenSpoke,
    })
);

/**
 * Action fired to initialize forward message for an event
 */
export const initializeEventForwardMessage = action(
    'INITIALIZE_EVENT_FORWARD_MESSAGE',
    (item: CalendarEvent) => ({
        item,
    })
);

/**
 * Fetch start action for calendar event card
 * @param partialEvent The partial event sent from present UI
 */
export const onFullEventFetchStart = action(
    'ON_FULL_EVENT_FETCH_START',
    (itemId: string, getCalendarEvents: GetCalendarEventsCallbackType) => ({
        itemId,
        getCalendarEvents,
    })
);

/**
 * Action fired when reading pane is closed
 */
export const onCloseReadingPane = action('ON_CLOSE_READING_PANE');
